"use client";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";

import { useMutation } from "@stadium/services/apollo-client";
import { useFlags } from "@stadium/services/launch-darkly";
import { apolloClient as client } from "@stadium/services/apollo-client/utils/instance";

import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";

import { UPDATE_PROFILE } from "@stadium/shared/src/graphql/mutations/updateProfile";

import { personalWorkspaceNudgeFlag } from "@stadium/services/launch-darkly/constants";

import { PersonalWorkspaceNudgeVariant } from "@stadium/shared/src/constants/personal-workspace-nudge";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";

import { shouldHidePersonalWorkspaceNudgeForPath } from "@stadium/shared/src/helpers/personal-workspace-nudge";

import nudgeSvg from "@stadium/shared/src/assets/images/nudge.svg";

import styles from "./main.module.scss";

export type PersonalWorkspaceNudgeProps = {
  variant:
    | typeof PersonalWorkspaceNudgeVariant.WEB
    | typeof PersonalWorkspaceNudgeVariant.RESPONSIVE;
  show: boolean;
};

const PersonalWorkspaceNudge = ({ variant, show }: PersonalWorkspaceNudgeProps) => {
  const router = useRouter();
  const { [personalWorkspaceNudgeFlag]: personalWorkspaceNudgeFlagEnabled } = useFlags();
  const { pathname } = router;
  const { currentUserData } = useCurrentUserContext();

  const personalWorkspaceNudgeLastDismissedAt =
    currentUserData?.profile?.personalWorkspaceNudgeLastDismissedAt;

  const [dismissed, setDismissed] = useState(true);

  const shouldHidePersonalWorkspaceNudge = shouldHidePersonalWorkspaceNudgeForPath(pathname);

  const [updateUser] = useMutation(UPDATE_PROFILE, {
    variables: { input: { input: { dismissPersonalWorkspaceNudge: true } } },
  });

  useEffect(() => {
    if (personalWorkspaceNudgeLastDismissedAt === undefined) return;

    setDismissed(Boolean(personalWorkspaceNudgeLastDismissedAt));
  }, [personalWorkspaceNudgeLastDismissedAt]);

  const handleDismiss = useCallback(() => {
    // Remove optimistically
    setDismissed(true);
    updateUser()
      .then((response) => {
        const updatedNudgeLastDismissedAt =
          response.data?.updateProfile?.user?.profile?.personalWorkspaceNudgeLastDismissedAt;
        if (!updatedNudgeLastDismissedAt) {
          setDismissed(false);
          return;
        }
        /** Update cache */
        client.cache.modify({
          id: client.cache.identify({
            __typename: "UserProfile",
            id: currentUserData?.profile?.id,
          }),
          fields: {
            personalWorkspaceNudgeLastDismissedAt() {
              return updatedNudgeLastDismissedAt;
            },
          },
        });
      })
      .catch(() => setDismissed(false));
  }, [currentUserData?.profile?.id, updateUser]);

  if (
    !show ||
    !personalWorkspaceNudgeFlagEnabled ||
    shouldHidePersonalWorkspaceNudge ||
    dismissed
  ) {
    return null;
  }

  return (
    <div
      tabIndex={0}
      aria-labelledby="titleId"
      aria-describedby="descriptionId"
      className={classNames(
        styles.nudge,
        variant === PersonalWorkspaceNudgeVariant.RESPONSIVE
          ? styles.nudgeResponsive
          : styles.nudgeWeb,
      )}
    >
      <div className={styles.nudgePointer} aria-hidden />
      <div className={styles.nudgeHeader}>
        <p className={styles.nudgeLabel}>Quick note</p>
        <button
          type="button"
          className={styles.dismissButton}
          aria-label="Dismiss personal workspace notice"
          onClick={handleDismiss}
        >
          <i className={styles.dismissIcon} aria-hidden />
        </button>
      </div>
      <div className={styles.nudgeContent}>
        <div className={styles.nudgeImageContainer}>
          <Multimedia
            type="image"
            src={nudgeSvg}
            height={80}
            width={80}
            alt="Nudge Icon"
            className={styles.nudgeImage}
            isNextImage={false}
          />
        </div>
        <div className={styles.nudgeTextContent}>
          <p id="titleId" className={styles.nudgeTitle}>
            You&apos;re in Personal Workspace
          </p>
          <p id="descriptionId" className={styles.nudgeDescription}>
            Not where you need to be? Switch here anytime to get back to your company Workspace.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PersonalWorkspaceNudge;
